import React, { useState, useEffect, useRef } from 'react';
import './NewBatch.css';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewBatch = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('auth-token');
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get("batchId");
  const isEditMode = !!batchId;
  const { selectedCenter } = useUser();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    whatsappLink: '',
    batchType: '',
    sports: '',
    proficiency: '',
    batchCapacity: '',
    monthlyFees: '',
    weekType: '',
    startTime: '',
    endTime: '',
    batchTimings: {
      Monday: { startTime: '', endTime: '', routines: [] },
      Tuesday: { startTime: '', endTime: '', routines: [] },
      Wednesday: { startTime: '', endTime: '', routines: [] },
      Thursday: { startTime: '', endTime: '', routines: [] },
      Friday: { startTime: '', endTime: '', routines: [] },
      Saturday: { startTime: '', endTime: '', routines: [] },
      Sunday: { startTime: '', endTime: '', routines: [] },
    },
    sessionPlans: {
      numberOfSessions: '',
      monthlyFee: '',
      quarterlyFee: '',
      halfYearlyFee: '',
      yearlyFee: '',
    },
    gstApplicable: false,
  });
  const [batchTypes, setBatchTypes] = useState([]);
  const [sportsList, setSportsList] = useState([]);
  const [proficiencyList, setProficiencyList] = useState([]);
  const [weekTypes, setWeekTypes] = useState([]);
  const [routineOptions, setRoutineOptions] = useState([]);
  const [selectedDays, setSelectedDays] = useState({
    Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false, Sunday: false,
  });
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const convertTimeTo12HourFormat = (timeString) => {
    if (!timeString) return '';
    const timePart = timeString.includes('T') ? timeString.split('T')[1] : timeString;
    const [hours, minutes] = timePart.split(':');
    let hour = parseInt(hours, 10);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${suffix}`;
  };

  const convertTo24HourFormat = (time) => {
    if (!time) return null;
    const [timePart, modifier] = time.split(' ');
    let [hours, minutes] = timePart.split(':');
    if (modifier === 'PM' && hours !== '12') hours = String(parseInt(hours, 10) + 12);
    if (modifier === 'AM' && hours === '12') hours = '00';
    return `${hours.padStart(2, '0')}:${minutes}`;
  };

  const weekDayMap = {
    Sunday: 7, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6,
  };

  const weekTypeToDaysMap = {
    WORKING_DAYS: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    WEEKEND: ['Saturday', 'Sunday'],
    WEEK_MWF: ['Monday', 'Wednesday', 'Friday'],
    WEEK_TTS: ['Tuesday', 'Thursday', 'Saturday'],
    WEEK_ALL: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    WEEK_TWTF: ['Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    WEEK_TTFS: ['Tuesday', 'Thursday', 'Friday', 'Saturday'],
    WEEK_TWTFS: ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    WEEK_MT: ['Monday', 'Tuesday'],
    WEEK_TF: ['Tuesday', 'Friday'],
    WEEK_MTTFS: ['Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'],
    WEEK_MTTF: ['Monday', 'Tuesday', 'Thursday', 'Friday'],
    WEEK_MTWTFS: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    WEEK_TTF: ['Tuesday', 'Thursday', 'Friday'],
  };

  const updateSelectedDays = (weekType) => {
    const daysToSelect = weekTypeToDaysMap[weekType] || [];
    const newSelectedDays = {
      Monday: daysToSelect.includes('Monday'),
      Tuesday: daysToSelect.includes('Tuesday'),
      Wednesday: daysToSelect.includes('Wednesday'),
      Thursday: daysToSelect.includes('Thursday'),
      Friday: daysToSelect.includes('Friday'),
      Saturday: daysToSelect.includes('Saturday'),
      Sunday: daysToSelect.includes('Sunday'),
    };
    setSelectedDays(newSelectedDays);
  };

  const sportsOptions = sportsList.map((sport) => ({ value: sport.id, label: sport.name }));

  const generateTimeOptions = () => {
    const options = [];
    let currentTime = new Date();
    currentTime.setHours(0, 0, 0, 0);
    for (let i = 0; i < 24 * 4; i++) {
      let hours = currentTime.getHours();
      let minutes = currentTime.getMinutes();
      let ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const timeString = `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
      options.push({ value: timeString, label: timeString });
      currentTime.setMinutes(currentTime.getMinutes() + 15);
    }
    return options;
  };
  const timeOptions = generateTimeOptions();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_API_BATCH_NEW}?academyId=${selectedCenter}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        const data = await resp.json();
        if (data.success) {
          setBatchTypes(data.data.batchType);
          setSportsList(data.data.sports);
          setProficiencyList(data.data.proficiencies);
          setWeekTypes(data.data.weekTypes);
          setRoutineOptions(
            data.data.routines.map(r => ({ value: r.id, label: r.name }))
          );
          setOptionsLoaded(true);
        }
      } catch (err) {
      }
    };
    fetchData();
  }, [selectedCenter, token]);

  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (!batchId) return;
      try {
        const resp = await fetch(`${process.env.REACT_APP_API_BATCH_EDIT}/${batchId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        const data = await resp.json();
        if (data.success) {
          const batchData = data.data.batch;
          const batchForm = data.data.batchForm;
          const sessionFees = batchForm.batchSessionFeesForms?.[0] || {};

          const weekDayIndexToName = {
            1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday',
          };

          const initialBatchTimings = { ...formData.batchTimings };
          batchForm.batchOperationForms.forEach((op) => {
            if (op.isChecked && op.weekDay) {
              const dayName = weekDayIndexToName[op.weekDay];
              if (dayName) {
                initialBatchTimings[dayName] = {
                  startTime: convertTimeTo12HourFormat(op.startTime),
                  endTime: convertTimeTo12HourFormat(op.endTime),
                  routines: op.routines ? op.routines.map(r => r.id) : [],
                };
              }
            }
          });

          setFormData((prev) => ({
            ...prev,
            name: batchData.name || '',
            whatsappLink: batchData.whatsAppUrl || '',
            batchType: batchData.batchType || '',
            sports: batchForm.sportId || '',
            proficiency: batchData.proficiencyType || '',
            batchCapacity: batchData.numberOfPlayers?.toString() || '',
            monthlyFees: batchData.monthlyFees?.toString() || '',
            weekType: batchData.weekType || '',
            startTime: convertTimeTo12HourFormat(batchData.startTime),
            endTime: convertTimeTo12HourFormat(batchData.endTime),
            gstApplicable: batchData.isGstApplicable || false,
            sessionPlans: {
              numberOfSessions: sessionFees.numberOfSessionsPerWeek?.toString() || '',
              monthlyFee: sessionFees.monthlyPerSessionFee?.toString() || '',
              quarterlyFee: sessionFees.quarterlyPerSessionFee?.toString() || '',
              halfYearlyFee: sessionFees.halfYearlyPerSessionFee?.toString() || '',
              yearlyFee: sessionFees.yearlyPerSessionFee?.toString() || '',
            },
            batchTimings: initialBatchTimings,
          }));

          updateSelectedDays(batchData.weekType);
          setEditLoaded(true);
        }
      } catch (err) {
      }
    };

    if (isEditMode && optionsLoaded) fetchBatchDetails();
  }, [batchId, isEditMode, optionsLoaded, token]); 

  const buildSubmitPayload = () => {
    const routineNameLookup = {};
    routineOptions.forEach((option) => {
      routineNameLookup[option.value] = option.label;
    });

    const batchOperationForms = Object.entries(formData.batchTimings)
      .filter(([day, timings]) => selectedDays[day] && timings.startTime && timings.endTime)
      .map(([day, timings]) => ({
        weekDay: weekDayMap[day] || null,
        startTime: `${new Date().toISOString().split('T')[0]}T${convertTo24HourFormat(timings.startTime)}:00`,
        endTime: `${new Date().toISOString().split('T')[0]}T${convertTo24HourFormat(timings.endTime)}:00`,
        isChecked: true,
        routines: (timings.routines || []).map((routineId) => ({
          id: routineId,
          name: routineNameLookup[routineId] || "Unknown Routine",
        })),
      }))
      .filter((entry) => entry.weekDay !== null);

    const formattedStartTime = convertTo24HourFormat(formData.startTime);
    const formattedEndTime = convertTo24HourFormat(formData.endTime);

    return {
      id: batchId || null,
      name: formData.name || '',
      academyId: selectedCenter || '',
      batchType: formData.batchType || '',
      startTime: formattedStartTime ? `${new Date().toISOString().split('T')[0]}T${formattedStartTime}:00` : null,
      endTime: formattedEndTime ? `${new Date().toISOString().split('T')[0]}T${formattedEndTime}:00` : null,
      sportId: formData.sports || '',
      numberOfPlayers: formData.batchCapacity ? parseInt(formData.batchCapacity, 10) : null,
      monthlyFees: formData.monthlyFees ? parseFloat(formData.monthlyFees) : null,
      weekType: formData.weekType || '',
      batchOperationForms,
      proficiencyType: formData.proficiency || '',
      batchSessionFeesForms: [
        {
          numberOfSessionsPerWeek: formData.sessionPlans.numberOfSessions ? parseInt(formData.sessionPlans.numberOfSessions, 10) : null,
          monthlyPerSessionFee: formData.sessionPlans.monthlyFee ? parseFloat(formData.sessionPlans.monthlyFee) : null,
          quarterlyPerSessionFee: formData.sessionPlans.quarterlyFee ? parseFloat(formData.sessionPlans.quarterlyFee) : null,
          halfYearlyPerSessionFee: formData.sessionPlans.halfYearlyFee ? parseFloat(formData.sessionPlans.halfYearlyFee) : null,
          yearlyPerSessionFee: formData.sessionPlans.yearlyFee ? parseFloat(formData.sessionPlans.yearlyFee) : null,
          monthlyPerSessionFeeId: null,
          quarterlyPerSessionFeeId: null,
          halfYearlyPerSessionFeeId: null,
          yearlyPerSessionFeeId: null,
        },
      ],
      operationalDays: batchOperationForms.map((op) => op.weekDay),
      isFlexible: true,
      isGstApplicable: !!formData.gstApplicable,
      whatsAppUrl: formData.whatsappLink || '',
    };
  };

  const redactForAudit = (payload) => {
    if (!payload) return payload;
    return { ...payload }; 
  };
  

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    const readyForBaseline = isEditMode ? (optionsLoaded && editLoaded) : optionsLoaded;
    if (readyForBaseline) {
      baselineRef.current = buildSubmitPayload();
      initializedBaselineRef.current = true;
    }
  }, [isEditMode, optionsLoaded, editLoaded, formData, selectedDays, routineOptions.length]);

  const handleRoutineChange = (selectedOptions, day) => {
    setFormData(prevData => ({
      ...prevData,
      batchTimings: {
        ...prevData.batchTimings,
        [day]: { ...prevData.batchTimings[day], routines: (selectedOptions || []).map(o => o.value) }
      }
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('sessionPlans')) {
      const sessionField = name.split('.')[1];
      setFormData(prev => ({ ...prev, sessionPlans: { ...prev.sessionPlans, [sessionField]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleBatchTimingChange = (e, day, field) => {
    const { value } = e.target;
    if (field === 'startTime' && formData.batchTimings[day].endTime === value) {
      Swal.fire({ icon: "error", title: "Error!", text: "Start time and end time cannot be the same", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (field === 'endTime' && formData.batchTimings[day].startTime === value) {
      Swal.fire({ icon: "error", title: "Error!", text: "End time cannot be the same as start time", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    setFormData(prev => ({
      ...prev,
      batchTimings: {
        ...prev.batchTimings,
        [day]: { ...prev.batchTimings[day], [field]: value }
      },
    }));
  };

  const handleTimeChange = (selectedOption, field) => {
    const newTime = selectedOption?.value || '';
    if (field === 'startTime' && formData.endTime === newTime) {
      Swal.fire({ icon: "error", title: "Error!", text: "Start time and end time cannot be the same", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (field === 'endTime' && formData.startTime === newTime) {
      Swal.fire({ icon: "error", title: "Error!", text: "End time cannot be the same as start time", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    setFormData((prev) => {
      const updatedData = { ...prev, [field]: newTime };
      const updatedStart = field === 'startTime' ? newTime : prev.startTime;
      const updatedEnd = field === 'endTime' ? newTime : prev.endTime;

      if (updatedStart && updatedEnd) {
        const updatedBatchTimings = { ...prev.batchTimings };
        Object.keys(selectedDays).forEach((day) => {
          if (selectedDays[day]) {
            updatedBatchTimings[day] = {
              ...updatedBatchTimings[day],
              startTime: updatedStart,
              endTime: updatedEnd,
            };
          }
        });
        updatedData.batchTimings = updatedBatchTimings;
      }
      return updatedData;
    });
  };

  const handleCheckboxChange = (day) => {
    setSelectedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  useEffect(() => {
    const numberOfSessions = Object.values(selectedDays).filter(Boolean).length;
    setFormData(prev => ({
      ...prev,
      sessionPlans: { ...prev.sessionPlans, numberOfSessions: numberOfSessions.toString() },
    }));
  }, [selectedDays]);

  const handleSubmit = async (e) => {
    e.preventDefault();


    const hasInvalidTimings = Object.entries(formData.batchTimings).some(
      ([day, timings]) => selectedDays[day] && timings.startTime && timings.endTime && timings.startTime === timings.endTime
    );
    if (!formData.batchType) return Swal.fire({ icon: "error", title: "Error!", text: "Batch Type is required!", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    if (hasInvalidTimings) return Swal.fire({ icon: "error", title: "Error!", text: "One or more days have the same start and end time", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    if (formData.startTime && formData.endTime && formData.startTime === formData.endTime)
      return Swal.fire({ icon: "error", title: "Error!", text: "Batch start time and end time cannot be the same", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    if (!formData.proficiency) return Swal.fire({ icon: "error", title: "Error!", text: "Proficiency is required!", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    if (!formData.weekType) return Swal.fire({ icon: "error", title: "Error!", text: "Week Type is required!", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    if (!formData.sports) return Swal.fire({ icon: "error", title: "Error!", text: "Please Select Sport!", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });

    const apiUrl = isEditMode
      ? process.env.REACT_APP_API_BATCH_EDIT
      : `${process.env.REACT_APP_API_BATCH_NEW}?academyId=${selectedCenter}`;

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    if (isEditMode) {
      const { digest, html } = buildDiffPreview(redactForAudit(oldPayload), redactForAudit(newPayload));
      if (!digest.length) {
        Swal.fire({ icon: 'info', title: 'Nothing to update', text: 'No differences detected.', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
        return;
      }
      const confirm = await Swal.fire({ title: 'Apply these changes?', html, showCancelButton: true, confirmButtonText: 'Update' });
      if (!confirm.isConfirmed) return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newPayload),
      });

      const result = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: isEditMode ? "Batch Updated successfully!" : "Batch Created successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        const affected_id =
          batchId ||
          result?.data?.id ||
          result?.data?.batchId ||
          result?.data?.batch?.id ||
          null;

        fireAudit({
          entity: 'batch',
          affected_id,
          action: isEditMode ? 'update' : 'create',
          businessApi: apiUrl,
          businessMethod: 'POST',
          oldPayload: redactForAudit(oldPayload),
          newPayload: redactForAudit(newPayload),
          section: 'Batches',
          subSection: 'New Batch',
          extra: { page: 'NewBatch' },
        });

        baselineRef.current = newPayload;

        navigate("/batchList");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: `Failed to ${isEditMode ? "update" : "create"} batch`,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: `Error occurred while ${isEditMode ? "updating" : "creating"} batch.`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/batchList" className="breadcrumb-link"> Batch List</Link> &gt; {isEditMode ? 'Edit Batch' : 'New Batch'}
      </div>

      <form onSubmit={handleSubmit} className="walkin-enquiry-form">
        {/* Name */}
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${formData.name ? 'has-value' : ''}`}
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter Name"
            required
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        {/* WhatsApp Group Link */}
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="url"
            name="whatsappLink"
            className={`walkin-form ${formData.whatsappLink ? 'has-value' : ''}`}
            value={formData.whatsappLink}
            onChange={handleInputChange}
            placeholder="Enter WhatsApp Group Link"
          />
          {formData.whatsappLink && <label htmlFor="whatsappLink">WhatsApp Group Link</label>}
        </div>

        {/* Batch Type */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="batchType"
            value={formData.batchType ? { value: formData.batchType, label: formData.batchType } : null}
            onChange={(opt) => setFormData({ ...formData, batchType: opt?.value || '' })}
            options={batchTypes.map((t) => ({ value: t, label: t }))}
            className={`walkin-form-dropdown ${formData.batchType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Batch Type"
            isClearable
            styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {formData.batchType && <label htmlFor="batchType">Batch Type</label>}
        </div>

        {/* Sport */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="sports"
            value={sportsOptions.find((o) => o.value.toString() === formData.sports.toString()) || null}
            onChange={(opt) => setFormData({ ...formData, sports: opt?.value || '' })}
            options={sportsOptions}
            className={`walkin-form-dropdown ${formData.sports ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Sport"
            isClearable
            styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {formData.sports && <label htmlFor="sports">Sport</label>}
        </div>

        {/* Proficiency */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="proficiency"
            value={formData.proficiency ? { value: formData.proficiency, label: formData.proficiency } : null}
            onChange={(opt) => setFormData({ ...formData, proficiency: opt?.value || '' })}
            options={proficiencyList.map((p) => ({ value: p, label: p }))}
            className={`walkin-form-dropdown ${formData.proficiency ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Proficiency"
            isClearable
            styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {formData.proficiency && <label htmlFor="proficiency">Proficiency</label>}
        </div>

        {/* Capacity */}
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="number"
            name="batchCapacity"
            className={`walkin-form ${formData.batchCapacity ? 'has-value' : ''}`}
            value={formData.batchCapacity}
            onChange={handleInputChange}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
            placeholder="Enter Batch Capacity"
            required
          />
          {formData.batchCapacity && <label htmlFor="batchCapacity">Batch Capacity*</label>}
        </div>

        {/* Monthly Fees */}
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="number"
            name="monthlyFees"
            className={`walkin-form ${formData.monthlyFees ? 'has-value' : ''}`}
            value={formData.monthlyFees}
            onChange={handleInputChange}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
            placeholder="Enter Monthly Fees"
            required
          />
          {formData.monthlyFees && <label htmlFor="monthlyFees">Monthly Fees*</label>}
        </div>

        {/* Week Type */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="weekType"
            value={formData.weekType ? { value: formData.weekType, label: formData.weekType } : null}
            onChange={(opt) => { setFormData({ ...formData, weekType: opt?.value || '' }); updateSelectedDays(opt?.value || ''); }}
            options={weekTypes.map((wt) => ({ value: wt, label: wt }))}
            className={`walkin-form-dropdown ${formData.weekType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Week Type"
            isClearable
            styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
          />
          {formData.weekType && <label htmlFor="weekType">Week Type</label>}
        </div>

        {/* Start/End time */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div className="floating-select-form" style={{ width: '98%', marginRight:'0px' }}>
            <Select
              name="startTime"
              value={timeOptions.find((option) => option.value === formData.startTime) || null}
              onChange={(opt) => handleTimeChange(opt, 'startTime')}
              options={timeOptions}
              className={`walkin-form-dropdown ${formData.startTime ? 'has-value' : ''}`}
              classNamePrefix="walkin-select"
              placeholder="Select Start Time"
              isClearable
              styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
            />
            {formData.startTime && <label htmlFor="startTime">Start Time</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%', marginRight:'0px' }}>
            <Select
              name="endTime"
              value={timeOptions.find((option) => option.value === formData.endTime) || null}
              onChange={(opt) => handleTimeChange(opt, 'endTime')}
              options={timeOptions}
              className={`walkin-form-dropdown ${formData.endTime ? 'has-value' : ''}`}
              classNamePrefix="walkin-select"
              placeholder="Select End Time"
              isClearable
              styles={{ control: (p) => ({ ...p, padding: '5px' }), placeholder: (p) => ({ ...p, color: 'black' }) }}
            />
            {formData.endTime && <label htmlFor="endTime">End Time</label>}
          </div>
        </div>

        {/* Batch Timings */}
        <h3 className="section-title">Batch Timings</h3>
        <div style={{ overflow: 'scroll' }}>
          <table className="batch-timings-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Week Day</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Routine</th>
              </tr>
            </thead>
            <tbody>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <tr key={day}>
                  <td>
                    <input type="checkbox" checked={selectedDays[day]} onChange={() => handleCheckboxChange(day)} className="day-checkbox" />
                  </td>
                  <td>{day}</td>
                  <td>
                    <Select
                      value={timeOptions.find(option => option.value === formData.batchTimings[day].startTime)}
                      onChange={(opt) => handleBatchTimingChange({ target: { value: opt?.value || '' } }, day, 'startTime')}
                      options={timeOptions}
                      placeholder="Select Start Time"
                      menuPortalTarget={document.body}
                    />
                  </td>
                  <td>
                    <Select
                      value={timeOptions.find(option => option.value === formData.batchTimings[day].endTime)}
                      onChange={(opt) => handleBatchTimingChange({ target: { value: opt?.value || '' } }, day, 'endTime')}
                      options={timeOptions}
                      placeholder="Select End Time"
                      menuPortalTarget={document.body}
                    />
                  </td>
                  <td>
                    <Select
                      isMulti
                      value={routineOptions.filter(option => formData.batchTimings[day].routines.includes(option.value))}
                      onChange={(selectedOptions) => handleRoutineChange(selectedOptions, day)}
                      options={routineOptions}
                      placeholder="Select Routine"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Weekly Session Plans */}
        <h3 className="section-title">Weekly Session Plans</h3>
        <div style={{ overflow: 'scroll' }}>
          <table className="session-plans-table">
            <thead>
              <tr>
                <th>Number Of Sessions (Per Week)</th>
                <th>Monthly (Per Session Fee)</th>
                <th>Quarterly (Per Session Fee)</th>
                <th>Half Yearly (Per Session Fee)</th>
                <th>Yearly (Per Session Fee)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><input type="text" value={formData.sessionPlans.numberOfSessions} onChange={handleInputChange} name="sessionPlans.numberOfSessions" className="form-input" required /></td>
                <td><input type="text" value={formData.sessionPlans.monthlyFee} onChange={handleInputChange} name="sessionPlans.monthlyFee" className="form-input" required /></td>
                <td><input type="text" value={formData.sessionPlans.quarterlyFee} onChange={handleInputChange} name="sessionPlans.quarterlyFee" className="form-input" required /></td>
                <td><input type="text" value={formData.sessionPlans.halfYearlyFee} onChange={handleInputChange} name="sessionPlans.halfYearlyFee" className="form-input" required /></td>
                <td><input type="text" value={formData.sessionPlans.yearlyFee} onChange={handleInputChange} name="sessionPlans.yearlyFee" className="form-input" required /></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* GST */}
        <label className="checkbox-label" style={{ width: '155px' }}>
          GST Applicable:
          <input
            type="checkbox"
            name="gstApplicable"
            checked={formData.gstApplicable}
            onChange={(e) => setFormData((prev) => ({ ...prev, gstApplicable: e.target.checked }))}
            className="checkbox-input"
          />
        </label>

        {/* Submit */}
        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isEditMode ? (isSubmitting ? 'Saving...' : 'Save Changes') : (isSubmitting ? 'Creating...' : 'Submit')}
        </button>
      </form>
    </div>
  );
};

export default NewBatch;
